import React from 'react';
import { Box, SpaceBetween, StatusIndicator } from '@amzn/awsui-components-react';
import { BulkPromotePhase } from 'src/models/promotion/bulkPromotionEnums';
import { getPhaseBand } from './bulkPromotionBands';
import { BulkPromotionContext } from 'src/services/bulk-promotion/BulkPromotionContext';
import { BulkPromoteStep } from 'src/services/bulk-promotion/BulkPromotionState';

export function formatBarrierWait(millis: number): string {
    /*
     * Guarded against a non-finite input, because Math.max propagates NaN rather than clamping it: a wait
     * that arrived as a string, undefined or Infinity rendered as "NaNs" — or "Infinitym NaNs" — on the
     * barrier screen. barrierWaitMillis is written straight through from the wire and validated nowhere,
     * and the existing "does not render a negative wait" test gave false confidence here because a clamp
     * is not a type check.
     */
    if (!Number.isFinite(millis)) {
        return 'an unknown time';
    }
    const totalSeconds = Math.max(0, Math.round(millis / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
}

/** Re-renders once a second while something is being waited for, so an elapsed time can elapse. */
function useElapsedMillis(since: number | null): number {
    const [now, setNow] = React.useState(() => Date.now());
    React.useEffect(() => {
        if (since === null) {
            return;
        }
        setNow(Date.now());
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, [since]);
    return since === null ? 0 : Math.max(0, now - since);
}

/**
 * Live phase and counts.
 *
 * Two waits get their own copy, because both are indistinguishable from a hung request otherwise and
 * the instinct in that ambiguity is to resubmit — which the service refuses, since only one batch runs
 * at a time account-wide, but which also discards this page's only handle on the batch still holding
 * that lock. AT_BARRIER names the parent components it is waiting on. The preflight wait names the
 * elapsed time: there are no intermediate progress messages to interpolate with, so the elapsed second
 * is the only honest evidence of life.
 *
 * The wait copy and the elapsed clock are gated on the step alone, NOT on `phase` being absent. They
 * used to require both, and SET_PHASE does not change the step — so the first RESOLVING or VALIDATING
 * frame silently removed the dropped-connection warning, the overdue advice and the clock, leaving a
 * frozen "Validating" indicator at exactly the moment the operator most needs to be told what to do.
 */
export default function BulkPromoteProgress() {
    const { bulkPromotionState } = React.useContext(BulkPromotionContext);
    const { phase, batchId, acceptedCount, barrierWaitMillis, step, awaitingVerdictSince, verdictOverdue, pushLost } =
        bulkPromotionState;

    const awaitingVerdict = step === BulkPromoteStep.AWAITING_VERDICT;
    const elapsed = useElapsedMillis(awaitingVerdict ? awaitingVerdictSince : null);

    /*
     * pushLost is in the gate, not just in the body. A drop can be reported before the first phase frame
     * lands and after the verdict wait has ended — step EXECUTING with phase still null — and without this
     * the component returned null and the advisory below never rendered at all, which is precisely the case
     * that advisory exists for.
     */
    if (!phase && !awaitingVerdict && !pushLost) {
        return null;
    }

    const [phaseLabel, phaseBand] = phase ? getPhaseBand(phase) : ['', 'in-progress' as const];
    const atBarrier = phase === BulkPromotePhase.AT_BARRIER;

    /*
     * Overdue and lost-push are not failures: the batch is still there either way, so neither is banded
     * error. What changes is the advice, which stops being "wait" and becomes "go and look".
     *
     * Split on whether the verdict is what is outstanding. Outside the verdict wait this headline is only
     * reached with no phase to show, which happens when a drop is reported before the first phase frame —
     * and the verdict wording would be wrong there, since the batch is past its verdict.
     */
    const [waitLabel, waitBand] = !awaitingVerdict
        ? (['Connection dropped while the batch was running', 'warning'] as const)
        : pushLost
          ? (['Connection dropped before the verdict arrived', 'warning'] as const)
          : verdictOverdue
            ? (['Still checking — no verdict yet', 'warning'] as const)
            : (['Checking components', 'in-progress'] as const);

    /*
     * The wait copy only replaces the phase while the VERDICT is what is outstanding.
     *
     * pushLost is accepted in CONFIRMING and EXECUTING as well, so without this the headline read
     * "Connection dropped before the verdict arrived" over a batch that was already past its verdict and
     * promoting — and every explanatory paragraph below is gated on awaitingVerdict, so that wrong headline
     * rendered alone with nothing under it. The phase is the accurate thing to show there; the drop is still
     * reported, by its own advisory further down.
     */
    const verdictWaitHeadline = awaitingVerdict && (pushLost || verdictOverdue);
    const showPhase = Boolean(phase) && !verdictWaitHeadline;

    return (
        <div className="resultsDiv">
            <div className="innerResultsDiv">
                <SpaceBetween direction="vertical" size="xs">
                    <div aria-live="polite" role="status">
                        {/*
                          * A lost push or an overdue verdict outranks the last phase seen. Preferring phase
                          * whenever it was set meant a dropped connection still banded in-progress as
                          * "Validating", so the headline contradicted the advisory directly below it.
                          */}
                        <StatusIndicator type={showPhase ? phaseBand : waitBand}>
                            {showPhase ? phaseLabel : waitLabel}
                        </StatusIndicator>
                    </div>
                    {atBarrier && (
                        <Box variant="p">
                            The batch is waiting for its parent components to be approved in the target before the
                            dependent components can go. It is progressing — resubmitting would be refused.
                        </Box>
                    )}
                    {atBarrier && barrierWaitMillis !== null && (
                        <Box variant="small">Waiting for {formatBarrierWait(barrierWaitMillis)}.</Box>
                    )}
                    {/*
                      * One advisory, chosen by precedence, rather than four independent tests. Written as
                      * separate conditions, pushLost and verdictOverdue both matched at once and stacked two
                      * contradictory paragraphs — "nothing has been written yet" immediately followed by
                      * "nothing has been cancelled". A lost connection is the more specific fact, so it wins.
                      */}
                    {awaitingVerdict && pushLost && (
                        <Box variant="p">
                            The verdict can no longer be delivered to this page. That does not by itself mean the
                            batch stopped — this page lost the connection, not the batch. Use <b>Get its outcome</b>{' '}
                            above to read its actual state instead of resubmitting.
                        </Box>
                    )}
                    {awaitingVerdict && !pushLost && verdictOverdue && (
                        <Box variant="p">
                            This is longer than a batch of this size usually takes. Nothing has been cancelled. Use{' '}
                            <b>Get its outcome</b> above to read the batch back if you would rather not keep waiting.
                        </Box>
                    )}
                    {awaitingVerdict && !pushLost && !verdictOverdue && (
                        <Box variant="p">
                            Every component is being checked against the target before anything is promoted. Nothing has
                            been written yet. This runs on the server, so it continues whether or not this page stays
                            open — resubmitting would be refused while this batch is open.
                        </Box>
                    )}
                    {awaitingVerdict && awaitingVerdictSince !== null && (
                        <Box variant="small">Waiting for {formatBarrierWait(elapsed)}.</Box>
                    )}
                    {/*
                      * A drop outside the verdict wait gets its own copy rather than borrowing the verdict
                      * wording. Gating the headline above would otherwise hide the drop entirely, since the
                      * four advisories are all for the verdict wait.
                      */}
                    {!awaitingVerdict && pushLost && (
                        <Box variant="p">
                            This page lost its connection while the batch was running, so it will not be told when
                            the batch finishes. The batch itself is unaffected — it runs on the server. Use{' '}
                            <b>Get its outcome</b> above to read its current state.
                        </Box>
                    )}
                    {acceptedCount > 0 && <Box variant="small">{acceptedCount} components accepted.</Box>}
                    {batchId && <Box variant="small">Batch {batchId}</Box>}
                </SpaceBetween>
            </div>
        </div>
    );
}
